// src/components/dashboard/ai_flow/JobCategorySelector.jsx
import React from "react";

const careerIcons = {
  "full stack developer": "💻",
  "software engineer": "⚙️",
  "frontend developer": "🎨",
  "front-end developer": "🎨",
  "backend developer": "🔧",
  "back-end developer": "🔧",
  "ai engineer": "🤖",
  "machine learning engineer": "🤖",
  "ai/machine learning engineer": "🤖",
  "artificial intelligence engineer": "🤖",
  "data scientist": "📊",
  "data analyst": "📈",
  "devops engineer": "☁️",
  "cloud engineer": "☁️",
  "mobile developer": "📱",
  "ios developer": "📱",
  "android developer": "📱",
  "ui designer": "🎨",
  "ux designer": "🎨",
  "ui/ux designer": "🎨",
  "product manager": "📋",
  "project manager": "📋",
  "cybersecurity specialist": "🛡️",
  "security engineer": "🛡️",
  "database administrator": "🗄️",
  "database developer": "🗄️",
  "quality assurance": "🔍",
  "qa engineer": "🔍",
  "test engineer": "🔍",
  "systems analyst": "🔬",
  "business analyst": "📊",
  "web developer": "🌐",
  "game developer": "🎮",
  "blockchain developer": "⛓️",
  "cloud architect": "☁️",
  "software architect": "🏗️",
  "technical writer": "✍️",
  "solutions architect": "🏗️",
  "network engineer": "🌐",
  "systems administrator": "⚙️",
  default: "💼",
};

function getCareerIcon(careerTitle) {
  const normalizedTitle = careerTitle.toLowerCase();
  return careerIcons[normalizedTitle] || careerIcons["default"];
}

// Enhanced CategoryCard component with improved styling and fixed hover issues
const CategoryCard = ({ category, isSelected, onSelect }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const cardStyles = {
    background: isSelected
      ? "linear-gradient(135deg, rgb(7, 50, 20), rgb(5, 35, 14))"
      : "white",
    color: isSelected ? "white" : "#2d3748",
    border: `2px solid ${
      isSelected
        ? "rgb(7, 50, 20)"
        : isHovered
        ? "rgb(229, 88, 18)"
        : "transparent"
    }`,
    borderRadius: "16px",
    padding: "30px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    minHeight: "240px", // Increased from 200px
    height: "100%", // Ensure all cards stretch to same height
    width: "100%", // Ensure card takes full width of grid cell
    boxSizing: "border-box", // Include padding in dimensions
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    overflow: "hidden",
    transform: isSelected || isHovered ? "translateY(-8px)" : "translateY(0)",
    boxShadow: isSelected
      ? "0 25px 50px rgba(7, 50, 20, 0.3)"
      : isHovered
      ? "0 20px 40px rgba(0, 0, 0, 0.15)"
      : "0 5px 15px rgba(0, 0, 0, 0.08)",
  };

  return (
    <div
      className="enhanced-category-card"
      onClick={() => onSelect(category)}
      style={cardStyles}
      onMouseEnter={() => !isSelected && setIsHovered(true)}
      onMouseLeave={() => !isSelected && setIsHovered(false)}
    >
      {/* Top gradient line */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: isSelected
            ? "rgba(255, 255, 255, 0.3)"
            : "linear-gradient(90deg, rgb(229, 88, 18), rgb(194, 65, 12))",
          transform: isSelected || isHovered ? "scaleX(1)" : "scaleX(0)",
          transition: "transform 0.3s ease",
          transformOrigin: "left",
        }}
      />

      <div>
        {/* Career Icon */}
        <div
          style={{
            width: "70px", // Increased from 60px
            height: "70px", // Increased from 60px
            borderRadius: "16px", // Increased from 12px
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "28px", // Increased from 24px
            marginBottom: "24px", // Increased from 20px
            background: isSelected
              ? "rgba(255, 255, 255, 0.2)"
              : "linear-gradient(135deg, rgb(229, 88, 18), rgb(194, 65, 12))",
            color: "white",
            transition: "all 0.3s ease",
          }}
        >
          {getCareerIcon(category.job_category)}
        </div>

        {/* Career Title */}
        <h4
          style={{
            margin: "0 0 12px 0", // Increased bottom margin
            fontSize: "1.4rem", // Increased from 1.3rem
            fontWeight: "700",
            transition: "color 0.3s ease",
            lineHeight: "1.3",
            minHeight: "2.8em", // Ensures space for 2 lines
            display: "flex",
            alignItems: "center",
          }}
        >
          {category.job_category}
        </h4>
      </div>

      {/* Match Container */}
      <div style={{ marginTop: "auto", paddingTop: "20px" }}>
        {" "}
        {/* Ensures this stays at bottom */}
        <div
          style={{
            fontSize: "0.95rem", // Slightly increased
            fontWeight: "600",
            marginBottom: "10px", // Increased from 8px
            opacity: 0.9,
          }}
        >
          Match Score
        </div>
        {/* Match Bar */}
        <div
          style={{
            height: "10px", // Increased from 8px
            background: isSelected
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
            borderRadius: "5px", // Increased from 4px
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${category.match_percentage}%`,
              background: isSelected
                ? "linear-gradient(90deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))"
                : "linear-gradient(90deg, rgb(7, 50, 20), rgb(5, 35, 14))",
              borderRadius: "5px",
              transition: "width 0.8s ease",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Shimmer effect */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
                animation: "shimmer 2s infinite",
              }}
            />
          </div>
        </div>
        {/* Match Percentage */}
        <div
          style={{
            fontSize: "1.2rem", // Increased from 1.1rem
            fontWeight: "700",
            marginTop: "10px", // Increased from 8px
          }}
        >
          {category.match_percentage}% Match
        </div>
      </div>
    </div>
  );
};

function JobCategorySelector({
  categories,
  selectedCategory,
  onSelectCategory,
  onGenerateResume,
  onSearchJobs,
  isLoading,
  isLoadingJobs,
  error,
  className = "",
}) {
  const [resumeButtonHovered, setResumeButtonHovered] = React.useState(false);
  const [jobsButtonHovered, setJobsButtonHovered] = React.useState(false);

  // Limit to 6 categories
  const displayCategories = categories.slice(0, 6);

  const resumeButtonStyles = {
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    display: "block",
    background:
      resumeButtonHovered && !isLoading && selectedCategory
        ? "linear-gradient(135deg, rgb(5, 35, 14), rgb(3, 25, 10))"
        : "linear-gradient(135deg, rgb(7, 50, 20), rgb(5, 35, 14))",
    color: "white",
    border: "none",
    padding: "20px 40px",
    borderRadius: "50px",
    fontSize: "1.1rem",
    fontWeight: "700",
    cursor: isLoading || !selectedCategory ? "not-allowed" : "pointer",
    transition: "all 0.3s ease",
    boxShadow:
      resumeButtonHovered && !isLoading && selectedCategory
        ? "0 15px 40px rgba(7, 50, 20, 0.4)"
        : "0 10px 30px rgba(7, 50, 20, 0.3)",
    opacity: isLoading || !selectedCategory ? 0.6 : 1,
    transform:
      resumeButtonHovered && !isLoading && selectedCategory
        ? "translateY(-3px)"
        : "translateY(0)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const jobsButtonStyles = {
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    display: "block",
    background:
      jobsButtonHovered && !isLoadingJobs && selectedCategory
        ? "linear-gradient(135deg, rgb(194, 65, 12), rgb(153, 51, 10))"
        : "linear-gradient(135deg, rgb(229, 88, 18), rgb(194, 65, 12))",
    color: "white",
    border: "none",
    padding: "20px 40px",
    borderRadius: "50px",
    fontSize: "1.1rem",
    fontWeight: "700",
    cursor: isLoadingJobs || !selectedCategory ? "not-allowed" : "pointer",
    transition: "all 0.3s ease",
    boxShadow:
      jobsButtonHovered && !isLoadingJobs && selectedCategory
        ? "0 15px 40px rgba(229, 88, 18, 0.4)"
        : "0 10px 30px rgba(229, 88, 18, 0.3)",
    opacity: isLoadingJobs || !selectedCategory ? 0.6 : 1,
    transform:
      jobsButtonHovered && !isLoadingJobs && selectedCategory
        ? "translateY(-3px)"
        : "translateY(0)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div
      className={`job-category-selector-section ${className}`}
      style={{
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "24px",
        padding: "40px",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        width: "100%", // Ensure full width
        boxSizing: "border-box", // Include padding in width calculation
      }}
    >
      {/* Enhanced Header */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <h3
          style={{
            fontSize: "2rem",
            color: "#2d3748",
            marginBottom: "15px",
            fontWeight: "700",
          }}
        >
          Select Your Target Career Path
        </h3>
        <p
          style={{
            fontSize: "1.1rem",
            color: "#718096",
            maxWidth: "600px",
            margin: "0 auto",
            lineHeight: "1.6",
          }}
        >
          Based on your profile, here are the top matching career paths. Select
          one to find relevant job opportunities or generate a tailored resume.
        </p>
      </div>

      {/* Enhanced Category Grid - Modified for 6 items */}
      <div
        className="category-list"
        style={{
          marginTop: "1rem",
          marginBottom: "40px",
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)", // Fixed 3 columns for desktop
          gap: "24px",
          alignItems: "stretch", // Ensures all cards stretch to same height
        }}
      >
        {displayCategories.map((cat, index) => (
          <div
            key={cat.job_category}
            style={{
              opacity: 0,
              transform: "translateY(30px)",
              animation: `fadeInUp 0.6s ease forwards`,
              animationDelay: `${index * 0.1}s`, // Reduced delay for 6 items
              width: "100%", // Ensure wrapper takes full width
            }}
          >
            <CategoryCard
              category={cat}
              isSelected={selectedCategory?.job_category === cat.job_category}
              onSelect={onSelectCategory}
            />
          </div>
        ))}
      </div>

      {/* Button Container */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Find Jobs Button - Primary Action */}
        {onSearchJobs && (
          <button
            onClick={onSearchJobs}
            disabled={isLoadingJobs || !selectedCategory}
            className="smai-button-primary"
            style={jobsButtonStyles}
            onMouseEnter={() => setJobsButtonHovered(true)}
            onMouseLeave={() => setJobsButtonHovered(false)}
          >
            {isLoadingJobs
              ? "🔍 Searching for Jobs..."
              : `🔍 Find Jobs for ${
                  selectedCategory ? selectedCategory.job_category : "..."
                }`}
          </button>
        )}

        {/* Generate Resume Button - Secondary Action */}
        <button
          onClick={onGenerateResume}
          disabled={isLoading || !selectedCategory}
          className="smai-button-secondary"
          style={resumeButtonStyles}
          onMouseEnter={() => setResumeButtonHovered(true)}
          onMouseLeave={() => setResumeButtonHovered(false)}
        >
          {isLoading
            ? "🔄 Generating Resume..."
            : `📄 Generate Resume for ${
                selectedCategory ? selectedCategory.job_category : "..."
              }`}
        </button>
      </div>

      {/* Enhanced Error Message */}
      {error && (
        <div
          className="error-message"
          style={{
            marginTop: "20px",
            padding: "15px",
            background: "rgba(254, 226, 226, 0.8)",
            border: "1px solid rgb(229, 88, 18)",
            borderRadius: "12px",
            color: "rgb(194, 65, 12)",
            fontSize: "0.9rem",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {/* Add keyframes for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        /* Responsive grid adjustments */
        @media (max-width: 1200px) {
          .category-list {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 768px) {
          .category-list {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }

          .enhanced-category-card {
            padding: 20px !important;
            min-height: 200px !important; /* Adjusted for mobile */
          }
        }
      `}</style>
    </div>
  );
}

export default JobCategorySelector;
